// @flow
import type { Element } from 'react';

import React from 'react';
import { Col, Form, Row, Table } from 'reactstrap';
import {
  ALLOW_COMPONENTS_TO_AFFECT_STRUCTURAL_PROPERTIES,
  BASE_SHEAR_STRENGTH_DIR_1,
  BASE_SHEAR_STRENGTH_DIR_2,
  BRB_TO_DIAPHRAGM_RATIO_DIR_1,
  BRB_TO_DIAPHRAGM_RATIO_DIR_2,
  DESIGN_PERIOD_DIR_1,
  DESIGN_PERIOD_DIR_2,
  LINES_OF_RESISTANCE_DIR_1,
  LINES_OF_RESISTANCE_DIR_2,
  MODE_SHAPE_METHOD,
  MODE_SHAPE_UPLOAD,
  TYPE_OF_RESISTANCE_DIR_1,
  TYPE_OF_RESISTANCE_DIR_2,
  YIELD_STORY_DRIFT_DIR_1,
  YIELD_STORY_DRIFT_DIR_2,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { Field, FormGroup } from '#components/pages/Models/Model/support/form/index.js';
import PeriodRow from './PeriodRow/index.jsx';
import ModeShapeUploader from './ModeShapeUploader/index.js';
import ModeShapeTable from './ModeShapeTable/index.js';
import ModeShapePlot from './ModeShapePlot/index.js';

type Props = {|
  fieldsByName: Object,
  form: string,
  heading: string,
  modelId: string,
  onBack: Function,
  onNext: Function,
  showModeShapeDetails: boolean,
|};

const StructuralPropertiesSection = (props: Props): Element<'div'> => {
  const { fieldsByName, form, heading, modelId, onBack, onNext, showModeShapeDetails } = props;

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <FormGroup
          tag="fieldset"
          form={form}
          fields={[ALLOW_COMPONENTS_TO_AFFECT_STRUCTURAL_PROPERTIES]}
        >
          <legend>Wood Light Frame Options</legend>
          <Field field={fieldsByName[ALLOW_COMPONENTS_TO_AFFECT_STRUCTURAL_PROPERTIES]} />
        </FormGroup>
        <FormGroup tag="fieldset">
          <legend>Structural Properties</legend>
          <Table responsive className="structural-properties">
            <thead>
              <tr>
                <th />
                <th>Direction 1</th>
                <th>Direction 2</th>
              </tr>
            </thead>
            <tbody>
              <FormGroup tag="tr" form={form} fields={[DESIGN_PERIOD_DIR_1, DESIGN_PERIOD_DIR_2]}>
                <td className="text-nowrap">
                  Design Model Period (T
                  <sub>{1}</sub>) <Tip tipId="designPeriod1" />
                </td>
                <td>
                  <Field field={fieldsByName[DESIGN_PERIOD_DIR_1]} excludeLabel />
                </td>
                <td>
                  <Field field={fieldsByName[DESIGN_PERIOD_DIR_2]} excludeLabel />
                </td>
              </FormGroup>
              <tr>
                <td>
                  Base Shear Strength <Tip tipId="baseShearStrength" />
                </td>
                <td>
                  <Field field={fieldsByName[BASE_SHEAR_STRENGTH_DIR_1]} excludeLabel />
                </td>
                <td>
                  <Field field={fieldsByName[BASE_SHEAR_STRENGTH_DIR_2]} excludeLabel />
                </td>
              </tr>
              <FormGroup
                tag="tr"
                form={form}
                fields={[YIELD_STORY_DRIFT_DIR_1, YIELD_STORY_DRIFT_DIR_2]}
              >
                <td>
                  Yield Story Drift <Tip tipId="yieldStoryDrift" />
                </td>
                <td>
                  <Field field={fieldsByName[YIELD_STORY_DRIFT_DIR_1]} excludeLabel />
                </td>
                <td>
                  <Field field={fieldsByName[YIELD_STORY_DRIFT_DIR_2]} excludeLabel />
                </td>
              </FormGroup>
              <PeriodRow fieldsByName={fieldsByName} form={form} period={1} />
              <PeriodRow fieldsByName={fieldsByName} form={form} period={2} />
              <PeriodRow fieldsByName={fieldsByName} form={form} period={3} />
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup
          tag="fieldset"
          form={form}
          fields={[
            LINES_OF_RESISTANCE_DIR_1,
            LINES_OF_RESISTANCE_DIR_2,
            TYPE_OF_RESISTANCE_DIR_1,
            TYPE_OF_RESISTANCE_DIR_2,
            BRB_TO_DIAPHRAGM_RATIO_DIR_1,
            BRB_TO_DIAPHRAGM_RATIO_DIR_2,
          ]}
        >
          <legend>Rigid-Wall Flexible Diaphragm Properties</legend>
          <Table responsive className="structural-properties">
            <thead>
              <tr>
                <th />
                <th>Direction 1</th>
                <th>Direction 2</th>
              </tr>
            </thead>
            <tbody>
              <FormGroup
                tag="tr"
                form={form}
                fields={[LINES_OF_RESISTANCE_DIR_1, LINES_OF_RESISTANCE_DIR_2]}
              >
                <td className="text-nowrap">
                  Interior Lines of LFRS <Tip tipId="linesOfResistance" />
                </td>
                <td>
                  <Field field={fieldsByName[LINES_OF_RESISTANCE_DIR_1]} excludeLabel />
                </td>
                <td>
                  <Field field={fieldsByName[LINES_OF_RESISTANCE_DIR_2]} excludeLabel />
                </td>
              </FormGroup>
              <FormGroup
                tag="tr"
                form={form}
                fields={[TYPE_OF_RESISTANCE_DIR_1, TYPE_OF_RESISTANCE_DIR_2]}
              >
                <td className="text-nowrap">
                  Type of Interior LFRS <Tip tipId="typeOfResistance" />
                </td>
                <td>
                  <Field field={fieldsByName[TYPE_OF_RESISTANCE_DIR_1]} excludeLabel />
                </td>
                <td>
                  <Field field={fieldsByName[TYPE_OF_RESISTANCE_DIR_2]} excludeLabel />
                </td>
              </FormGroup>
              <FormGroup
                tag="tr"
                form={form}
                fields={[BRB_TO_DIAPHRAGM_RATIO_DIR_1, BRB_TO_DIAPHRAGM_RATIO_DIR_2]}
              >
                <td>
                  BRB/Diaph. Strength Ratio <Tip tipId="brbToDiaphragmRatio" />
                </td>
                <td>
                  <Field field={fieldsByName[BRB_TO_DIAPHRAGM_RATIO_DIR_1]} excludeLabel />
                </td>
                <td>
                  <Field field={fieldsByName[BRB_TO_DIAPHRAGM_RATIO_DIR_2]} excludeLabel />
                </td>
              </FormGroup>
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup tag="fieldset" form={form} fields={[MODE_SHAPE_METHOD]}>
          <legend>
            Mode Shapes <Tip tipId="modeShapes" />
          </legend>
          <Row>
            <Col lg="6">
              <Field field={fieldsByName[MODE_SHAPE_METHOD]} />
            </Col>
          </Row>
          <FormGroup form={form} fields={[MODE_SHAPE_UPLOAD]}>
            <ModeShapeUploader
              form={form}
              name={MODE_SHAPE_UPLOAD}
              modelId={modelId}
              accept=".csv"
              maxBytes={3e6}
            />
          </FormGroup>
          {showModeShapeDetails && (
            <div>
              <div className="pt-2 pb-2">
                <h5>Direction 1</h5>
                <ModeShapePlot modelId={modelId} direction={1} key="model-shape-plot-dir-1" />
                <ModeShapeTable modelId={modelId} direction={1} key="model-shape-table-dir-1" />
              </div>
              <div className="pt-2 pb-2">
                <h5>Direction 2</h5>
                <ModeShapePlot modelId={modelId} direction={2} key="model-shape-plot-dir-2" />
                <ModeShapeTable modelId={modelId} direction={2} key="model-shape-table-dir-2" />
              </div>
            </div>
          )}
        </FormGroup>

        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default StructuralPropertiesSection;
